import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Grid,
  IconButton,
  Autocomplete,
} from "@mui/material";

import useAxios from "../../helpers/Hooks/useAxios";
import { useForm, Controller } from "react-hook-form";
import CloseIcon from "@mui/icons-material/Close";
import { URLS } from "../../helpers/URLS";
import { toast } from "react-toastify";
import { DEPARTMENTS, DESIGNATIONS, optionLabel } from "../../helpers/orgOptions";
import "../../assets/scss/tables.scss";

const AddEmployeeForm = ({ open, onClose, editData = null, onSuccess }) => {
  const isEdit = Boolean(editData);
  const {
    handleSubmit,
    control,
    register,
    reset,
    setValue,
    setError,
    formState: { errors },
  } = useForm();

  const { fetchData } = useAxios();

  /* who this person can report to */
  const [managers, setManagers] = useState([]);

  useEffect(() => {
    if (!open) return;
    const getManagers = async () => {
      const resp = await fetchData({ url: URLS.getEmpoyeesLight, method: "get" });
      setManagers(resp?.data || []);
    };
    getManagers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (open) {
      if (editData) {
        reset({
          companyName: editData.companyName || "Motivitylabs Private Limited",
          employeeName: editData.employeeName || "",
          employeeCode: editData.employeeCode || "",
          emailID: editData.emailID || "",
          countryCode: editData.countryCode || "91",
          mobile: editData.mobile || "",
          managerEmail: editData.managerEmail || "",
          designation: editData.designation || "",
          department: editData.department || "",
          officeLocation: editData.officeLocation || "Motivity Labs, Hyderabad",
          workLocation: editData.workLocation || "Hyderabad",
        });
      } else {
        reset({
          companyName: "Motivitylabs Private Limited",
          employeeName: "",
          employeeCode: "",
          emailID: "",
          countryCode: "91",
          mobile: "",
          managerEmail: "",
          designation: "",
          department: "",
          officeLocation: "Motivity Labs, Hyderabad",
          workLocation: "Hyderabad",
        });
      }
    }
  }, [open, editData, reset]);

  const onSubmit = async (data) => {
    try {
      const url = isEdit ? URLS.updateEmployee : URLS.addEmployee;
      const method = isEdit ? "put" : "post";
      const resp = await fetchData({
        url,
        method,
        body: { countryCode: "91", ...data },
      });
      if (resp.success || resp.status === 200) {
        toast.success(isEdit ? "Employee Updated Successfully" : "Employee Added Successfully");
        reset();
        if (onSuccess) onSuccess();
        onClose();
      } else {
        toast.error(resp.message || "Operation failed");
      }
    } catch (error) {
      toast.error(error?.message || "An error occurred");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" className="wh-dialog">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="wh-dialog-head">
          <div className="wh-dialog-headings">
            <h5>{isEdit ? "Edit Employee" : "Add Employee"}</h5>
            <p>{isEdit ? "Update employee details below" : "They will be emailed a password once the record is created"}</p>
          </div>
          <IconButton className="wh-dialog-close" aria-label="close" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </div>
        <DialogContent>
          <Grid container columnSpacing={2} rowSpacing={0.5} sx={{ mt: 0 }}>
            {/* Company Name */}
            <Grid item xs={6}>
              <TextField
                size="small"
                label="Company Name"
                fullWidth
                defaultValue={"Motivitylabs Private Limited"}
                {...register("companyName")}
                helperText=" "
              />
            </Grid>

            {/* Employee Name */}
            <Grid item xs={6}>
              <TextField
                size="small"
                label="Employee Name *"
                fullWidth
                InputLabelProps={{ shrink: true }}
                {...register("employeeName", {
                  required: "Employee Name is required",
                })}
                error={!!errors.employeeName}
                helperText={errors.employeeName?.message || " "}
              />
            </Grid>

            {/* Employee Code */}
            <Grid item xs={12} md={4}>
              <TextField
                size="small"
                label="Employee Code *"
                fullWidth
                disabled={isEdit}
                InputLabelProps={{ shrink: true }}
                {...register("employeeCode", {
                  required: "Employee Code is required",
                })}
                error={!!errors.employeeCode}
                helperText={errors.employeeCode?.message || " "}
              />
            </Grid>

            {/* Experience */}
            <Grid item xs={12} md={4}>
              <TextField
                size="small"
                label="Experience"
                placeholder="6 Years"
                fullWidth
                {...register("experience")}
                helperText=" "
              />
            </Grid>

            {/* Mobile Number */}
            <Grid item xs={12} md={4}>
              <TextField
                size="small"
                label="Mobile Number (+91) *"
                placeholder="9876543210"
                fullWidth
                InputLabelProps={{ shrink: true }}
                {...register("mobile", {
                  required: "Mobile Number is required",
                })}
                error={!!errors.mobile}
                helperText={errors.mobile?.message || " "}
              />
            </Grid>

            {/* Email */}
            <Grid item xs={6}>
              <TextField
                size="small"
                label="Email *"
                fullWidth
                {...register("emailID", {
                  required: "Email is required",
                  pattern: {
                    value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
                    message: "Invalid email format",
                  },
                })}
                error={!!errors.email}
                helperText={errors.email?.message || " "}
              />
            </Grid>

            {/* Reports To (Manager Email) */}
            <Grid item xs={6}>
              <Controller
                name="managerEmail"
                control={control}
                defaultValue=""
                render={({ field: { onChange, value } }) => (
                  <Autocomplete
                    size="small"
                    freeSolo
                    autoHighlight
                    options={managers}
                    getOptionLabel={(option) =>
                      typeof option === "string"
                        ? option
                        : option?.emailID || ""}
                    /* name on top, email beneath: the email is what gets stored,
                       but the name is what the admin is looking for */
                    renderOption={(props, option) => (
                      <li {...props} key={option._id || option.emailID}>
                        <span className="wh-ac-person">
                          <b>{option.employeeName}</b>
                          <em>
                            {option.designation || "Role not set"}
                            {option.emailID ? <span> · {option.emailID}</span> : null}
                          </em>
                        </span>
                      </li>
                    )}
                    filterOptions={(options, state) => {
                      const q = state.inputValue.trim().toLowerCase();
                      if (!q) return options;
                      return options.filter((o) =>
                        `${o.employeeName || ""} ${o.emailID || ""} ${o.employeeCode || ""} ${o.designation || ""}`
                          .toLowerCase().includes(q));
                    }}
                    value={value || ""}
                    onChange={(e, picked) => {
                      if (picked && typeof picked !== "string") {
                        onChange(picked.emailID || "");
                        /* the record stores the name too, so set both */
                        setValue("managerName", picked.employeeName || "");
                        return;
                      }
                      onChange(typeof picked === "string" ? picked : "");
                    }}
                    onInputChange={(e, typed) => onChange(typed)}
                    componentsProps={{ paper: { className: "wh-ac-paper" } }}
                    ListboxProps={{ className: "wh-ac-listbox" }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        label="Reports To"
                        placeholder="Search by name or email"
                        helperText=" "
                      />
                    )}
                  />
                )}
              />
              <input type="hidden" {...register("managerName")} />
            </Grid>

            {/* Designation */}
            <Grid item xs={6}>
              <Controller
                name="designation"
                control={control}
                defaultValue=""
                rules={{ required: "Designation is required" }}
                render={({ field: { onChange, value } }) => (
                  <Autocomplete
                    size="small"
                    freeSolo
                    autoHighlight
                    options={DESIGNATIONS}
                    groupBy={(option) => option.group}
                    getOptionLabel={optionLabel}
                    value={value || ""}
                    onChange={(e, picked) => onChange(optionLabel(picked))}
                    onInputChange={(e, typed) => onChange(typed)}
                    componentsProps={{ paper: { className: "wh-ac-paper" } }}
                    ListboxProps={{ className: "wh-ac-listbox" }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        label="Designation *"
                        placeholder="Start typing…"
                        error={!!errors.designation}
                        helperText={errors.designation?.message || " "}
                      />
                    )}
                  />
                )}
              />
            </Grid>

            {/* Department */}
            <Grid item xs={6}>
              <Controller
                name="department"
                control={control}
                defaultValue=""
                rules={{ required: "Department is required" }}
                render={({ field: { onChange, value } }) => (
                  <Autocomplete
                    size="small"
                    freeSolo
                    autoHighlight
                    options={DEPARTMENTS}
                    getOptionLabel={optionLabel}
                    value={value || ""}
                    onChange={(e, picked) => onChange(optionLabel(picked))}
                    onInputChange={(e, typed) => onChange(typed)}
                    componentsProps={{ paper: { className: "wh-ac-paper" } }}
                    ListboxProps={{ className: "wh-ac-listbox" }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        label="Department *"
                        placeholder="Start typing…"
                        error={!!errors.department}
                        helperText={errors.department?.message || " "}
                      />
                    )}
                  />
                )}
              />
            </Grid>

            {/* Office Location */}
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel id="office-location-label">Office Location</InputLabel>
                <Controller
                  name="officeLocation"
                  control={control}
                  defaultValue="Motivity Labs, Hyderabad"
                  render={({ field }) => (
                    <Select {...field} size="small">
                      <MenuItem value="Motivity Labs, Hyderabad">
                        Motivity Labs, Hyderabad
                      </MenuItem>
                      <MenuItem value="Motivity Labs, USA">Motivity Labs, USA</MenuItem>
                    </Select>
                  )}
                />
              </FormControl>
            </Grid>

            {/* Work Location */}
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel id="work-location-label">Work Location</InputLabel>
                <Controller
                  name="workLocation"
                  control={control}
                  defaultValue="Hyderabad"
                  render={({ field }) => (
                    <Select {...field} size="small">
                      <MenuItem value="Hyderabad">
                        Hyderabad
                      </MenuItem>
                      <MenuItem value="Dallas, USA">Dallas, USA</MenuItem>
                    </Select>
                  )}
                />
              </FormControl>
            </Grid>

            {/* Country Dropdown */}

            {/* Gender */}
            {/* <Grid item xs={3}>
              <FormControl component="fieldset">
                Gender: 
                <Controller
                  name="gender"
                  control={control}
                  rules={{ required: "Gender is required" }}
                  render={({ field }) => (
                    <RadioGroup row {...field}>
                      <FormControlLabel
                        value="Male"
                        control={<Radio />}
                        label="Male"
                      />
                      <FormControlLabel
                        value="Female"
                        control={<Radio />}
                        label="Female"
                      />
                    </RadioGroup>
                  )}
                />
                {errors.gender && (
                  <Typography color="error">{errors.gender.message}</Typography>
                )}
              </FormControl>
            </Grid> */}
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} variant="contained" color="inherit">
            Cancel
          </Button>
          <Button type="submit" variant="contained">
            {isEdit ? "Update Employee" : "Add Employee"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddEmployeeForm;
